import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
} from '@mui/material'
import {
  Close as CloseIcon,
  EditOutlined,
  FileDownloadOutlined,
  FolderZipOutlined,
  Inventory2Outlined,
  TravelExploreOutlined,
  VisibilityOutlined,
} from '@mui/icons-material'
import dayjs from 'dayjs'
import toast from 'react-hot-toast'
import axiosInstant from '../../api'
import {
  rmnAiPresenceReportEndpoint,
  rmnAiPresenceReportsEndpoint,
  rmnBrandAiPresenceEndpoint,
  rmnBrandAiPresenceTargetsEndpoint,
  rmnBrandConsumerVoiceEndpoint,
  rmnBrandConsumerVoiceTargetsEndpoint,
  rmnBrandCrawlEndpoint,
  rmnBrandEndpoint,
  rmnBrandPrepPacksEndpoint,
  rmnBrandsEndpoint,
  rmnConsumerVoiceReportEndpoint,
  rmnConsumerVoiceReportsEndpoint,
  rmnImportBrandUrlEndpoint,
  rmnPrepPackEndpoint,
} from '../../api/endpoints'
import { CvScoreChip } from '../../components/ConsumerVoice/CvScoreChip'
import { ConsumerVoiceHistorySection } from '../../components/ConsumerVoice/ConsumerVoiceHistorySection'
import { ConsumerVoiceReportDialog } from '../../components/ConsumerVoice/ConsumerVoiceReportDialog'
import { downloadConsumerVoicePdf } from '../../components/ConsumerVoice/consumerVoiceFiles'
import { downloadPrepPackById, runBrandIntelligence } from '../../components/PrepPack/prepPackActions'
import { ConsumerVoiceAudit } from '../../types/consumerVoice'
import { AiPresenceAudit } from '../../types/aiPresence'
import { IconPlus, IconRefresh } from '../../utils/icons'
import { RunningBadge } from '../../components/RunningBadge'
import { MainContainer } from '../../components/ContentWrapper'
import { ListPageHeader } from '../../components/ListPageHeader/ListPageHeader'
import { CatalogTabs, BRAND_CATALOG_TABS } from './CatalogTabs'
import { DataTable, DataTableColumn } from '../../components/DataTable/DataTable'
import { ReportHistorySection } from '../../components/PdpAudit/ReportHistorySection'
import { downloadRmnReportById } from '../../components/PdpAudit/reportFiles'
import { BrandReportForm } from '../../components/PdpAudit/BrandReportForm'
import { BrandCvRunForm } from '../../components/ConsumerVoice/BrandCvRunForm'
import { BrandAipRunForm } from '../../components/AiPresence/BrandAipRunForm'
import { AipScoreChip } from '../../components/AiPresence/AipScoreChip'
import { AiPresenceHistorySection } from '../../components/AiPresence/AiPresenceHistorySection'
import { AiPresenceReportDialog } from '../../components/AiPresence/AiPresenceReportDialog'
import { downloadAiPresencePdf } from '../../components/AiPresence/aiPresenceFiles'
import { ReportViewerDialog } from '../../components/PdpAudit/ReportViewerDialog'
import { useNetworkScope } from '../../hooks/useNetworkScope'
import { initialsFromName } from '../../utils'
import { RmnNetworkBrand } from '../../types/rmn'
import { EditUrlDialog } from './EditUrlDialog'
import { ScoreChip, isDisplayableUrl, viewsDiscoveredProducts } from './catalogShared'
import {
  crawlFailureMessage,
  crawlRowLabel,
  crawlStatusLabel,
  isBrandKindCrawl,
  isCrawlInProgress,
  isSiteCrawl,
  isCrawlStoppable,
  requestCrawlStop,
  useRmnCrawls,
} from './useRmnCrawls'

const SITE_CRAWL_MESSAGE =
  'A full catalog analysis is running — try again when it finishes.'

// Per-row shelf re-crawl button state (mirrors the Categories page's):
// one analysis per target, so only the brand being analyzed disables —
// plus every row while an admin full-site analysis runs.
const brandCrawlTitle = (
  crawlable: boolean,
  rowActive: boolean,
  siteCrawlActive: boolean,
): string => {
  if (!crawlable) return 'Set this brand\'s shelf URL first (edit the Brand URL).'
  if (rowActive) return 'This brand\'s shelf is being analyzed — new products appear when it finishes.'
  if (siteCrawlActive) return SITE_CRAWL_MESSAGE
  return 'Analyze this brand\'s shelf page again — picks up new products.'
}

// Catalog › Brands (TDC-1499): RMN networks only — the network root and
// its associated supplier brands (brands_rmn_brands) via GET /rmn/brands,
// now with average PDP scores, product counts, an editable brand-page URL
// ("where the crawl starts" for that brand on the retailer site), and a
// per-row brand report. Finished reports live in this page's Brand Reports
// section below the table. Non-RMN users never see the nav entry; a direct
// hit bounces to Categories. All new row fields are optional so the page
// renders against an API that predates them.

export default function NetworkBrandsListPage() {
  const navigate = useNavigate()
  // Workspace-aware (useNetworkScope): the opened brand inside
  // /portal/:slug, else the user's own; basePath prefixes catalog links.
  const {
    brandId: accountBrandId,
    isNetwork: isRmnUser,
    basePath,
    permittedBrandIds,
  } = useNetworkScope()
  // A supplier allowlist (internal Brand > Team) makes brand adds 403.
  const canAddBrand = permittedBrandIds === null
  const [rows, setRows] = useState<RmnNetworkBrand[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [reportBrand, setReportBrand] = useState<RmnNetworkBrand | null>(null)
  // Run Brand CV Analysis: the product picker (BrandCvRunForm).
  const [cvBrand, setCvBrand] = useState<RmnNetworkBrand | null>(null)
  // Run Brand AI Presence Analysis: the product picker (BrandAipRunForm).
  const [aipBrand, setAipBrand] = useState<RmnNetworkBrand | null>(null)
  const [viewingAipId, setViewingAipId] = useState<number | null>(null)
  const [aipRefreshKey, setAipRefreshKey] = useState(0)
  const [editingBrand, setEditingBrand] = useState<RmnNetworkBrand | null>(null)
  // Run Brand PDP Analysis on a brand with no shelf URL on file: prompt for
  // it (like the product launcher's URL prompt), save it, then analyze.
  const [urlPromptBrand, setUrlPromptBrand] = useState<RmnNetworkBrand | null>(null)
  const [addingBrand, setAddingBrand] = useState(false)
  const [reportsRefreshKey, setReportsRefreshKey] = useState(0)
  // Brands with a generating report — fed by the reports section below (it
  // polls while anything generates, so buttons re-enable on their own).
  const [runningIds, setRunningIds] = useState<Set<number>>(new Set())
  const [downloadingReportId, setDownloadingReportId] = useState<number | null>(null)
  const [viewingReportId, setViewingReportId] = useState<number | null>(null)
  const handleDownloadReport = async (b: RmnNetworkBrand) => {
    if (!b.latest_report || downloadingReportId) return
    setDownloadingReportId(b.latest_report.id)
    try {
      await downloadRmnReportById(b.latest_report.id)
    } finally {
      setDownloadingReportId(null)
    }
  }

  // Consumer Voice + Brand Intelligence (one click, no form). Both run in
  // the background; the rows carry cv_running / prep_pack_running, so we
  // reload the list every 8s while anything is in flight.
  const [cvRefreshKey, setCvRefreshKey] = useState(0)
  const [viewingCvId, setViewingCvId] = useState<number | null>(null)
  const [startingIds, setStartingIds] = useState<Set<string>>(new Set())
  const startKey = (kind: string, id: number) => `${kind}:${id}`
  const markStarting = (key: string, on: boolean) =>
    setStartingIds((cur) => {
      const next = new Set(cur)
      if (on) next.add(key)
      else next.delete(key)
      return next
    })
  const handleCreateStrategyFromCv = (audit: ConsumerVoiceAudit) => {
    const params = new URLSearchParams({ cv: String(audit.id) })
    if (audit.desireid) params.set('desireid', String(audit.desireid))
    navigate(`${basePath}/opportunities/strategies/new?${params.toString()}`)
  }
  const handleCreateStrategyFromAip = (audit: AiPresenceAudit) => {
    const params = new URLSearchParams({ aip: String(audit.id) })
    if (audit.desireid) params.set('desireid', String(audit.desireid))
    navigate(`${basePath}/opportunities/strategies/new?${params.toString()}`)
  }

  const loadBrands = useCallback(async () => {
    try {
      const res = await axiosInstant.get(rmnBrandsEndpoint)
      setRows((res.data || []) as RmnNetworkBrand[])
      setError(null)
    } catch {
      setError("Failed to load your network's brands. Please try again.")
    }
  }, [])

  const startBrandConsumerVoice = (b: RmnNetworkBrand) => setCvBrand(b)
  const startBrandIntelligence = async (b: RmnNetworkBrand) => {
    const key = startKey('bi', b.id)
    if (startingIds.has(key)) return
    markStarting(key, true)
    try {
      await runBrandIntelligence(rmnBrandPrepPacksEndpoint(b.id), b.name)
      await loadBrands()
    } finally {
      markStarting(key, false)
    }
  }
  // Rows carry cv_running / prep_pack_running; poll the list while any is
  // in flight so the buttons re-enable and the report dates fill in.
  const anyIntelRunning = (rows || []).some((b) => b.cv_running || b.aip_running || b.prep_pack_running)
  useEffect(() => {
    if (!anyIntelRunning) return undefined
    const t = setInterval(() => {
      loadBrands()
    }, 8000)
    return () => clearInterval(t)
  }, [anyIntelRunning, loadBrands])

  // The report list polls while anything generates; when a brand's run
  // leaves that set, reload the rows so its Last run flips to ready/failed
  // and the download icon lights up without a manual refresh.
  const prevRunningRef = useRef<Set<number>>(new Set())
  const handleReportRows = useCallback(
    (reportRows: { status: string; params?: any }[]) => {
      const next = new Set(
        reportRows
          .filter((r) => r.status === 'generating')
          .map((r) => r.params && r.params.brand_id)
          .filter((id): id is number => typeof id === 'number'),
      )
      const finished = [...prevRunningRef.current].some((id) => !next.has(id))
      prevRunningRef.current = next
      setRunningIds(next)
      if (finished) loadBrands()
    },
    [loadBrands],
  )

  useEffect(() => {
    if (!isRmnUser) return
    loadBrands()
  }, [isRmnUser, loadBrands])

  // Add-brand crawls run in the background (crawl, then the import step) —
  // show them above the table and reload the list when one settles.
  const { crawls, refresh: refreshCrawls } = useRmnCrawls({
    onFinished: (crawl) => {
      // Per-row shelf re-crawls: crawl only, nothing imports afterwards.
      if (crawl.kind === 'brand-crawl') {
        if (crawl.status === 'completed') {
          toast.success(
            crawl.stopped
              ? 'Analysis stopped — everything it found was kept.'
              : `Analysis finished — ${crawl.brand_name || 'the brand'}'s products are up to date.`,
          )
          loadBrands()
        } else {
          toast.error(crawlFailureMessage(crawl))
        }
        return
      }
      if (crawl.kind !== 'brand') return
      const summary = crawl.url_import
      if (crawl.status === 'completed' && summary && !summary.error) {
        const base = summary.brand_name
          ? `Added ${summary.brand_name} — ${summary.products_imported || 0} products imported.`
          : 'Brand analysis finished.'
        toast.success(
          summary.report_id ? `${base} The brand report is generating below.` : base,
        )
        loadBrands()
        // The auto-created report lands in Brand Reports with a Generating
        // badge that polls until ready.
        if (summary.report_id) setReportsRefreshKey((k) => k + 1)
      } else if (
        summary &&
        (summary.error === 'BrandNotOnPage' || summary.error === 'NoBrandFound')
      ) {
        const named = crawl.brand_name ? `“${crawl.brand_name}”` : 'that brand'
        toast.error(
          crawl.stopped
            ? `Analysis stopped before ${named} was found — try again and let it run a little longer.`
            : `We couldn't find ${named} on that page. Check the spelling, or that the URL points at that brand's shelf page.`,
        )
      } else {
        toast.error(crawlFailureMessage(crawl))
      }
    },
  })
  const brandCrawls = crawls.filter((c) => isBrandKindCrawl(c) && isCrawlInProgress(c))
  const siteCrawlActive = crawls.some((c) => isCrawlInProgress(c) && isSiteCrawl(c))
  const activeBrandIds = new Set(
    brandCrawls.map((c) => c.brand_id).filter((id): id is number => typeof id === 'number'),
  )

  // Why the modal's Analyze action is unavailable (mirrors the row button).
  const brandAnalyzeDisabledReason = (b: RmnNetworkBrand): string | null => {
    if (!isDisplayableUrl(b.brand_url)) return 'Set a Brand URL we can analyze first (edit the Brand URL).'
    if (activeBrandIds.has(b.id) || siteCrawlActive) {
      return 'An analysis is already running — try again when it finishes.'
    }
    return null
  }

  const startBrandCrawl = async (b: RmnNetworkBrand) => {
    try {
      await axiosInstant.post(rmnBrandCrawlEndpoint(b.id))
      toast.success(`Analysis started for ${b.name} — new products appear after it finishes.`)
      refreshCrawls()
    } catch (e: any) {
      const status = e?.response?.status
      const code = e?.response?.data
      if (status === 409) {
        toast.error(
          'This brand is already being analyzed, or a full catalog analysis is running — try again when it finishes.',
        )
      } else if (code === 'BrandNotCrawlable') {
        toast.error('This brand doesn\'t have a URL we can analyze — edit its Brand URL first.')
      } else if (code === 'DomainNotAllowed') {
        toast.error('This brand\'s URL isn\'t on your network\'s domains.')
      } else if (status === 429) {
        toast.error('You\'ve reached the hourly limit for URL imports. Try again later.')
      } else {
        toast.error('Couldn\'t start the analysis. Please try again.')
      }
      refreshCrawls()
    }
  }

  if (!isRmnUser) return <Navigate to={`${basePath}/products`} replace />

  const saveBrandUrl = async (url: string) => {
    if (!editingBrand) return
    try {
      const res = await axiosInstant.patch(rmnBrandEndpoint(editingBrand.id), {
        brand_url: url || null,
      })
      const saved = (res.data && res.data.brand_url) || (url || null)
      setRows((cur) =>
        cur ? cur.map((b) => (b.id === editingBrand.id ? { ...b, brand_url: saved } : b)) : cur,
      )
      toast.success(
        url ? `Updated the URL for ${editingBrand.name}` : `Cleared the URL for ${editingBrand.name}`,
      )
    } catch (e: any) {
      if (e?.response?.status === 400) {
        throw new Error('Enter a full URL starting with http:// or https://')
      }
      if (e?.response?.status === 403) {
        throw new Error("You don't have permission to edit this brand's URL.")
      }
      throw new Error("Couldn't save the URL. Please try again.")
    }
  }

  const columns: DataTableColumn<RmnNetworkBrand>[] = [
    {
      key: 'brand',
      header: 'Brand',
      render: (b) => (
        <div className="flex items-center gap-[10px]">
          <div className="w-[30px] h-[30px] rounded-[7px] bg-tint text-accent-text text-[11px] font-bold flex items-center justify-center shrink-0 overflow-hidden">
            {b.logo ? (
              <img src={b.logo} alt="" className="w-full h-full object-contain" />
            ) : (
              initialsFromName(b.name)
            )}
          </div>
          <span className="font-semibold text-text-1">{b.name}</span>
        </div>
      ),
    },
    {
      key: 'score',
      header: 'PDP Score',
      // Score chip plus the Run Brand PDP Analysis icon (same refresh icon
      // the intel footers and the Categories page use). Disabled while a
      // report is generating; a brand with no shelf URL on file gets the
      // URL prompt first (see the urlPromptBrand flow). The latest READY
      // report's date + view/download sit under the chip so the column
      // reads as one "PDP" block (the CV column mirrors it).
      render: (b) => {
        const reportRunning = runningIds.has(b.id)
        return (
          <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1.5">
            {typeof b.avg_score === 'number' ? (
              <>
                <ScoreChip score={b.avg_score} />
                {(b.scored_product_count || 0) > 0 && (
                  <span className="text-[11px] text-muted">{b.scored_product_count} scored</span>
                )}
              </>
            ) : (
              <span className="text-muted">—</span>
            )}
            {reportRunning ? (
              <RunningBadge
                label="Analyzing"
                title="An analysis is running for this brand — it will appear in Brand Reports below when it finishes."
              />
            ) : (
              <Tooltip title="Run Brand PDP Analysis — pick which of this brand's catalog products to analyze and roll the findings into one report.">
                <button
                  type="button"
                  aria-label={`Run ${b.name} PDP analysis`}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (isDisplayableUrl(b.brand_url)) setReportBrand(b)
                    else setUrlPromptBrand(b)
                  }}
                  className="w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 shrink-0"
                >
                  <IconRefresh size={13} />
                </button>
              </Tooltip>
            )}
          </div>
          {b.latest_report && (
          <div className="flex items-center gap-1">
            <span
              className="text-[11px] text-text-2 whitespace-nowrap"
              title={`Latest PDP report: ${dayjs(b.latest_report.created_at).format('MMM DD, YYYY h:mm A')}`}
            >
              {dayjs(b.latest_report.created_at).format('MMM DD, YYYY')}
            </span>
            <Tooltip title="View the latest report">
              <button
                type="button"
                aria-label={`View latest ${b.name} report`}
                onClick={() => setViewingReportId(b.latest_report!.id)}
                className="w-6 h-6 flex items-center justify-center rounded text-[#9B958C] hover:bg-[#F4F2EE] shrink-0"
              >
                <VisibilityOutlined sx={{ fontSize: 15 }} />
              </button>
            </Tooltip>
            <Tooltip title="Download the latest report">
              <span>
                <button
                  type="button"
                  aria-label={`Download latest ${b.name} report`}
                  onClick={() => handleDownloadReport(b)}
                  disabled={downloadingReportId !== null}
                  className="w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                >
                  <FileDownloadOutlined sx={{ fontSize: 15 }} />
                </button>
              </span>
            </Tooltip>
          </div>
          )}
          </div>
        )
      },
    },
    {
      key: 'cv_score',
      header: 'CV Score',
      // Score chip plus the Run Brand CV Analysis icon, with the latest
      // analysis's date + view/download underneath (same layout as PDP).
      render: (b) => (
        <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-1.5">
          {typeof b.cv_score === 'number' ? (
            <>
              <CvScoreChip
                score={b.cv_score}
                title="Consumer Voice Score: mean of this brand's products' latest analyses"
              />
              {(b.cv_scored_product_count || 0) > 0 && (
                <span className="text-[11px] text-muted">{b.cv_scored_product_count} scored</span>
              )}
            </>
          ) : (
            <span className="text-muted">—</span>
          )}
          {b.cv_running ? (
            <RunningBadge
              label="Analyzing"
              title="A Consumer Voice analysis is running for this brand — it appears in Consumer Voice History below when it finishes."
            />
          ) : (
            <Tooltip title="Run Brand CV Analysis — read what shoppers say on Reddit about several of this brand's products and roll it into one score.">
              {/* span: MUI Tooltip needs a hoverable child even when disabled */}
              <span className="inline-flex">
                <button
                  type="button"
                  aria-label={`Run ${b.name} CV analysis`}
                  onClick={(e) => {
                    e.stopPropagation()
                    startBrandConsumerVoice(b)
                  }}
                  disabled={startingIds.has(startKey('cv', b.id))}
                  className="w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                >
                  <IconRefresh size={13} />
                </button>
              </span>
            </Tooltip>
          )}
        </div>
          {b.cv_latest_report && (
            <div className="flex items-center gap-1">
              <span
                className="text-[11px] text-text-2 whitespace-nowrap"
                title={`Latest Consumer Voice analysis: ${dayjs(b.cv_latest_report.created_at).format('MMM DD, YYYY h:mm A')}`}
              >
                {dayjs(b.cv_latest_report.created_at).format('MMM DD, YYYY')}
              </span>
              <Tooltip title="View the latest Consumer Voice analysis">
                <button
                  type="button"
                  aria-label={`View latest ${b.name} Consumer Voice analysis`}
                  onClick={() => setViewingCvId(b.cv_latest_report!.id)}
                  className="w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 shrink-0"
                >
                  <VisibilityOutlined sx={{ fontSize: 15 }} />
                </button>
              </Tooltip>
              <Tooltip title="Download the latest Consumer Voice analysis">
                <button
                  type="button"
                  aria-label={`Download latest ${b.name} Consumer Voice analysis`}
                  onClick={() => downloadConsumerVoicePdf(rmnConsumerVoiceReportEndpoint(b.cv_latest_report!.id))}
                  className="w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 shrink-0"
                >
                  <FileDownloadOutlined sx={{ fontSize: 15 }} />
                </button>
              </Tooltip>
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'aip_score',
      header: 'AI Presence',
      // Same layout as the CV column: score chip + run icon, latest date +
      // view/download underneath.
      render: (b) => (
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1.5">
            {typeof b.aip_score === 'number' ? (
              <>
                <AipScoreChip score={b.aip_score} title="AI Presence Score: mean of this brand's products' latest analyses" />
                {(b.aip_scored_product_count || 0) > 0 && (
                  <span className="text-[11px] text-muted">{b.aip_scored_product_count} scored</span>
                )}
              </>
            ) : (
              <span className="text-muted">—</span>
            )}
            {b.aip_running ? (
              <RunningBadge
                label="Analyzing"
                title="An AI Presence analysis is running for this brand — it appears in AI Presence History below when it finishes."
              />
            ) : (
              <Tooltip title="Run Brand AI Presence Analysis — how often Claude, Gemini and OpenAI surface and recommend this brand's products.">
                <span className="inline-flex">
                  <button
                    type="button"
                    aria-label={`Run ${b.name} AI Presence analysis`}
                    onClick={(e) => {
                      e.stopPropagation()
                      setAipBrand(b)
                    }}
                    className="w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                  >
                    <IconRefresh size={13} />
                  </button>
                </span>
              </Tooltip>
            )}
          </div>
          {b.aip_latest_report && (
            <div className="flex items-center gap-1">
              <span className="text-[11px] text-text-2 whitespace-nowrap">
                {dayjs(b.aip_latest_report.created_at).format('MMM DD, YYYY')}
              </span>
              <Tooltip title="View the latest AI Presence analysis">
                <button
                  type="button"
                  aria-label={`View latest ${b.name} AI Presence analysis`}
                  onClick={() => setViewingAipId(b.aip_latest_report!.id)}
                  className="w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 shrink-0"
                >
                  <VisibilityOutlined sx={{ fontSize: 15 }} />
                </button>
              </Tooltip>
              <Tooltip title="Download the latest AI Presence analysis">
                <button
                  type="button"
                  aria-label={`Download latest ${b.name} AI Presence analysis`}
                  onClick={() => downloadAiPresencePdf(rmnAiPresenceReportEndpoint(b.aip_latest_report!.id))}
                  className="w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 shrink-0"
                >
                  <FileDownloadOutlined sx={{ fontSize: 15 }} />
                </button>
              </Tooltip>
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'imported',
      header: 'Catalog',
      render: (b) => (
        <div className="flex items-center gap-1">
          <span
            className="text-[12px] text-text-2"
            title="Products in your catalog under this brand"
          >
            {b.imported_product_count || 0}
          </span>
          <Tooltip
            title={
              viewsDiscoveredProducts(b.imported_product_count)
                ? `Nothing imported for ${b.name} yet — view its discovered products`
                : `View ${b.name} products`
            }
          >
            <button
              type="button"
              aria-label={`View ${b.name} products`}
              // The account's own row means "everything" — plain Products
              // (the filter dropdown only lists sub brands). A brand with
              // nothing imported goes to Discovered, where its crawled
              // rows are waiting.
              onClick={() => {
                let path = `${basePath}/products?rmn_brand_id=${b.id}`
                if (b.id === accountBrandId) path = `${basePath}/products`
                else if (viewsDiscoveredProducts(b.imported_product_count)) {
                  path = `${basePath}/products/discovered?brand=${b.id}`
                }
                navigate(path)
              }}
              className="w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 shrink-0"
            >
              <Inventory2Outlined sx={{ fontSize: 15 }} />
            </button>
          </Tooltip>
        </div>
      ),
    },
    {
      key: 'discovered',
      header: 'Discovered',
      render: (b) =>
        b.discovered_product_count ? (
          <span
            className="text-[12px] text-text-2"
            title="Found by the analysis, not yet in your catalog — review them on the Discovered tab"
          >
            {b.discovered_product_count}
          </span>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      key: 'brand_intel',
      header: 'Prep Pack',
      // Same layout as the score columns minus the score: run icon on top,
      // latest pack's date + download underneath.
      render: (b) => (
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1.5">
            {!b.latest_prep_pack && !b.prep_pack_running && <span className="text-muted">—</span>}
            {b.prep_pack_running ? (
              <RunningBadge
                label="Generating"
                title="A Sales Intel Prep Pack is generating for this brand — download it here when it finishes."
              />
            ) : (
              <Tooltip title="Run Prep Pack — generate the Sales Intel Prep Pack from this brand's catalog, no form to fill in.">
                {/* span: MUI Tooltip needs a hoverable child even when disabled */}
                <span className="inline-flex">
                  <button
                    type="button"
                    aria-label={`Run ${b.name} Sales Intel Prep Pack`}
                    onClick={(e) => {
                      e.stopPropagation()
                      startBrandIntelligence(b)
                    }}
                    disabled={startingIds.has(startKey('bi', b.id))}
                    className="w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                  >
                    <IconRefresh size={13} />
                  </button>
                </span>
              </Tooltip>
            )}
          </div>
          {b.latest_prep_pack && (
            <div className="flex items-center gap-1">
              <span
                className="text-[11px] text-text-2 whitespace-nowrap"
                title={`Latest Sales Intel Prep Pack: ${dayjs(b.latest_prep_pack.created_at).format('MMM DD, YYYY h:mm A')}`}
              >
                {dayjs(b.latest_prep_pack.created_at).format('MMM DD, YYYY')}
              </span>
              <Tooltip title="Download the latest Sales Intel Prep Pack (ZIP)">
                <button
                  type="button"
                  aria-label={`Download latest ${b.name} Sales Intel Prep Pack`}
                  onClick={() => downloadPrepPackById(rmnPrepPackEndpoint(b.latest_prep_pack!.id), { internal: false })}
                  className="w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 shrink-0"
                >
                  <FolderZipOutlined sx={{ fontSize: 15 }} />
                </button>
              </Tooltip>
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'brand_url',
      header: 'Brand URL',
      render: (b) => (
        <div className="flex items-center gap-1 max-w-[240px]">
          {isDisplayableUrl(b.brand_url) ? (
            <a
              href={b.brand_url}
              target="_blank"
              rel="noopener noreferrer"
              title={b.brand_url}
              className="text-[12px] text-accent-text truncate hover:underline"
            >
              {b.brand_url.replace(/^https?:\/\/(www\.)?/, '')}
            </a>
          ) : (
            <span className="text-muted">—</span>
          )}
          <Tooltip
            title={brandCrawlTitle(
              isDisplayableUrl(b.brand_url),
              activeBrandIds.has(b.id),
              siteCrawlActive,
            )}
          >
            <span className="inline-flex">
              <button
                type="button"
                aria-label={`Analyze ${b.name}`}
                onClick={() => startBrandCrawl(b)}
                disabled={
                  !isDisplayableUrl(b.brand_url) || activeBrandIds.has(b.id) || siteCrawlActive
                }
                className="w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
              >
                <TravelExploreOutlined sx={{ fontSize: 15 }} />
              </button>
            </span>
          </Tooltip>
          <Tooltip title="Edit brand URL">
            <button
              type="button"
              aria-label={`Edit ${b.name} URL`}
              onClick={() => setEditingBrand(b)}
              className="w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 shrink-0"
            >
              <EditOutlined sx={{ fontSize: 15 }} />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ]

  // GET /rmn/brands returns the network root itself too — it doubles as the
  // prep-pack / filter brand picker (same drop as ProductFilters and
  // StrategyFilters). Catalog > Brands is a list of supplier brands: neither
  // the account's own brand nor any rmn:true parent belongs in it.
  const items = (rows || []).filter((b) => b.id !== accountBrandId && !b.rmn)

  return (
    <MainContainer>
      <ListPageHeader
        title="Brands"
        subtitle={
          items.length > 0
            ? `Brands in your catalog · ${items.length} total. Import discovered brands to run PDP intelligence.`
            : 'Brands in your catalog. Import discovered brands to run PDP intelligence.'
        }
        actionLabel={canAddBrand ? 'Add brand' : undefined}
        onActionClick={canAddBrand ? () => setAddingBrand(true) : undefined}
        actionIcon={<IconPlus width={16} height={16} stroke="#fff" />}
        actionDisabled={siteCrawlActive}
        actionTitle={siteCrawlActive ? SITE_CRAWL_MESSAGE : undefined}
        tabs={<CatalogTabs tabs={BRAND_CATALOG_TABS} />}
      />

      {/* In-flight brand adds: crawl → import step → row appears below. */}
      {brandCrawls.map((crawl) => (
        <div
          key={`crawl-${crawl.id}`}
          className="flex items-center gap-3 border border-border-token rounded-lg px-3 py-2.5 mb-2 bg-surface"
        >
          <CircularProgress size={14} sx={{ color: 'var(--accent-text)' }} />
          <span className="flex-1 min-w-0 text-[13px] text-text-2 truncate">
            {crawlRowLabel(crawl)}…
          </span>
          <span className="text-[11px] font-bold uppercase tracking-[0.5px] text-accent-text bg-tint rounded-full px-2.5 py-1 shrink-0">
            {crawlStatusLabel(crawl)}
          </span>
          <span className="text-[12px] text-muted shrink-0">
            {crawl.pages_fetched > 0 ? `${crawl.pages_fetched} pages` : ''}
          </span>
          {isCrawlStoppable(crawl) && (
            <button
              type="button"
              title="Stop this analysis — the brand and products it already found will still be imported."
              onClick={async () => {
                if (await requestCrawlStop(crawl)) refreshCrawls()
              }}
              className="text-[12px] font-semibold text-text-2 border border-border-token rounded-md px-2.5 py-1.5 bg-transparent cursor-pointer hover:bg-surface-2 shrink-0"
            >
              Stop
            </button>
          )}
        </div>
      ))}

      <DataTable<RmnNetworkBrand>
        columns={columns}
        rows={items}
        loading={rows === null && !error}
        totalCount={items.length}
        hasMore={false}
        error={error}
        onLoadMore={() => undefined}
        emptyMessage="No brands in your network yet"
        getRowKey={(r) => r.id}
      />

      <div className="mt-10">
        <ReportHistorySection
          reportType="brand"
          title="Brand Report History"
          refreshKey={reportsRefreshKey}
          onRowsChange={handleReportRows}
        />
      </div>

      <div className="mt-10">
        <ConsumerVoiceHistorySection
          listEndpoint={rmnConsumerVoiceReportsEndpoint}
          itemEndpoint={rmnConsumerVoiceReportEndpoint}
          refreshKey={cvRefreshKey}
          onCreateStrategy={handleCreateStrategyFromCv}
        />
      </div>
      <div className="mt-10">
        <AiPresenceHistorySection
          listEndpoint={rmnAiPresenceReportsEndpoint}
          itemEndpoint={rmnAiPresenceReportEndpoint}
          refreshKey={aipRefreshKey}
          onCreateStrategy={handleCreateStrategyFromAip}
        />
      </div>
      {viewingAipId !== null && (
        <AiPresenceReportDialog
          endpoint={rmnAiPresenceReportEndpoint(viewingAipId)}
          productEndpoint={rmnAiPresenceReportEndpoint}
          onClose={() => setViewingAipId(null)}
          onCreateStrategy={handleCreateStrategyFromAip}
        />
      )}
      {viewingCvId !== null && (
        <ConsumerVoiceReportDialog
          endpoint={rmnConsumerVoiceReportEndpoint(viewingCvId)}
          productEndpoint={rmnConsumerVoiceReportEndpoint}
          onClose={() => setViewingCvId(null)}
          onCreateStrategy={handleCreateStrategyFromCv}
          createStrategyLabel="Create strategy"
        />
      )}

      <Dialog open={!!reportBrand} onClose={() => setReportBrand(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {reportBrand ? `Brand PDP Analysis — ${reportBrand.name}` : 'Brand PDP Analysis'}
          <IconButton aria-label="Close" onClick={() => setReportBrand(null)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {reportBrand && (
            <BrandReportForm
              brandId={reportBrand.id}
              brandName={reportBrand.name}
              discoveredCount={reportBrand.discovered_product_count || 0}
              onAnalyze={() => {
                startBrandCrawl(reportBrand)
                setReportBrand(null)
              }}
              analyzeDisabledReason={brandAnalyzeDisabledReason(reportBrand)}
              onCreated={() => {
                setReportBrand(null)
                // The report lands in this page's own list with a
                // Generating badge that polls until ready.
                setReportsRefreshKey((k) => k + 1)
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!cvBrand} onClose={() => setCvBrand(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {cvBrand ? `Brand CV Analysis — ${cvBrand.name}` : 'Brand CV Analysis'}
          <IconButton aria-label="Close" onClick={() => setCvBrand(null)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {cvBrand && (
            <BrandCvRunForm
              brandName={cvBrand.name}
              targetsEndpoint={rmnBrandConsumerVoiceTargetsEndpoint(cvBrand.id)}
              runEndpoint={rmnBrandConsumerVoiceEndpoint(cvBrand.id)}
              emptyAction={{ label: 'Add a product', onClick: () => navigate(`${basePath}/products`) }}
              onCreated={async () => {
                setCvBrand(null)
                await loadBrands()
                setCvRefreshKey((k) => k + 1)
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!aipBrand} onClose={() => setAipBrand(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {aipBrand ? `Brand AI Presence Analysis — ${aipBrand.name}` : 'Brand AI Presence Analysis'}
          <IconButton aria-label="Close" onClick={() => setAipBrand(null)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {aipBrand && (
            <BrandAipRunForm
              brandName={aipBrand.name}
              targetsEndpoint={rmnBrandAiPresenceTargetsEndpoint(aipBrand.id)}
              runEndpoint={rmnBrandAiPresenceEndpoint(aipBrand.id)}
              emptyAction={{ label: 'Add a product', onClick: () => navigate(`${basePath}/products`) }}
              onCreated={async () => {
                setAipBrand(null)
                await loadBrands()
                setAipRefreshKey((k) => k + 1)
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <EditUrlDialog
        open={!!urlPromptBrand}
        title={urlPromptBrand ? `Run Brand PDP Analysis — ${urlPromptBrand.name}` : 'Run Brand PDP Analysis'}
        label="Brand shelf page URL"
        helperText="This brand has no page on file yet — paste its shelf page on the retailer site and we'll analyze it to discover its products. Import the ones you want, then run the analysis."
        initialUrl=""
        allowClear={false}
        saveLabel="Save & analyze"
        savingLabel="Starting…"
        onClose={() => setUrlPromptBrand(null)}
        onSave={async (url) => {
          if (!urlPromptBrand) return
          const brand = urlPromptBrand
          let saved: string | null = null
          try {
            const res = await axiosInstant.patch(rmnBrandEndpoint(brand.id), { brand_url: url })
            saved = (res.data && res.data.brand_url) || url
          } catch (e: any) {
            if (e?.response?.status === 400) {
              throw new Error('Enter a full URL starting with http:// or https://')
            }
            if (e?.response?.status === 403) {
              throw new Error("You don't have permission to edit this brand's URL.")
            }
            throw new Error("Couldn't save the URL. Please try again.")
          }
          setRows((cur) => (cur ? cur.map((b) => (b.id === brand.id ? { ...b, brand_url: saved } : b)) : cur))
          await startBrandCrawl({ ...brand, brand_url: saved })
          setUrlPromptBrand(null)
        }}
      />

      <EditUrlDialog
        open={!!editingBrand}
        title={editingBrand ? `Brand URL — ${editingBrand.name}` : 'Brand URL'}
        label="Brand page URL"
        helperText="Where this brand's products live on the retailer site — where the analysis starts for the brand."
        initialUrl={editingBrand && isDisplayableUrl(editingBrand.brand_url) ? editingBrand.brand_url : ''}
        onClose={() => setEditingBrand(null)}
        onSave={saveBrandUrl}
      />

      <EditUrlDialog
        open={addingBrand}
        title="Add brand"
        showNameField
        nameLabel="Brand name"
        label="Brand page URL"
        helperText="Paste the brand's shelf page on the retailer site — we'll find that brand on it, add it to your network and import up to 10 of its products. This runs in the background and takes a few minutes."
        initialUrl=""
        allowClear={false}
        saveLabel="Start analysis"
        savingLabel="Starting…"
        onClose={() => setAddingBrand(false)}
        onSave={async (url, name) => {
          try {
            await axiosInstant.post(rmnImportBrandUrlEndpoint, { url, name })
            toast.success(
              'Analysis started — the brand and its products will appear here in a few minutes.',
            )
            // Light the placeholder row right away.
            refreshCrawls()
          } catch (e: any) {
            const status = e?.response?.status
            const code = e?.response?.data
            if (status === 409) {
              throw new Error(
                'That URL is already being analyzed, or a full catalog analysis is running — try again when it finishes.',
              )
            }
            if (code === 'DomainNotAllowed') {
              throw new Error('That URL isn\'t on your network\'s domains.')
            }
            if (status === 403) {
              throw new Error('Only retail network accounts can add brands by URL.')
            }
            if (status === 429) {
              throw new Error('You\'ve reached the hourly limit for URL imports. Try again later.')
            }
            throw new Error('Couldn\'t start the analysis. Please try again.')
          }
        }}
      />
      {viewingReportId !== null && (
        <ReportViewerDialog reportId={viewingReportId} onClose={() => setViewingReportId(null)} />
      )}
    </MainContainer>
  )
}
